import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { OptionalAppShell } from '../../../components/layout/AppShell';
import { studentV2NavItems } from '../../../components/layout/v2NavItems';
import { isUiRedesignPageEnabled } from '../../../config/uiRedesign';
import { lessonRunsApi } from '../../../services/apiV2';
import { ResourceToolkit, LessonResource } from '../../../components/v2/ResourceToolkit';
import { StepRenderer } from '../../../components/v2/StepRenderer';

type ReviewStep = {
  id: number;
  order: number;
  title: string;
  stage_tag?: string | null;
  runner_phase?: 'live' | 'studio';
  step_kind: string;
  counts_as_evidence: boolean;
  content: Record<string, any>;
  signal_config: Record<string, any>;
  evidence_config: Record<string, any>;
  requires_submission: boolean;
  submission_status: 'submitted' | 'no_submission' | 'not_required';
  signal?: {
    response: Record<string, any>;
    submitted_at?: string | null;
  } | null;
  evidence?: {
    id: number;
    status: string;
    evidence_type: string;
    content: Record<string, any>;
    submitted_at?: string | null;
  } | null;
};

type ReviewPayload = {
  run: {
    id: number;
    status: string;
    started_at: string;
    ended_at?: string | null;
    lesson_plan_id: number;
    lesson_plan_title: string;
    section_id: number;
    section_name: string;
    review_access: 'off' | 'after_end' | 'always';
    allow_catchup: boolean;
    show_assessment_answers_in_review: boolean;
  };
  student_id: number;
  source: 'snapshot' | 'live_plan_fallback';
  catchup_assignment?: {
    assignment_id: number;
    delivery_mode: 'evidence_only' | 'replay_exam' | 'replay_lesson';
    due_at?: string | null;
    is_active: boolean;
    is_targeted: boolean;
    student_status?: 'not_started' | 'in_progress' | 'completed' | 'excused' | null;
  } | null;
  steps: ReviewStep[];
  studio_completion?: {
    display_label: string;
  } | null;
  resources?: LessonResource[];
};

const statusStyles: Record<string, string> = {
  submitted: 'bg-emerald-100 text-emerald-700',
  no_submission: 'bg-amber-100 text-amber-700',
  not_required: 'bg-slate-100 text-slate-600',
};

const statusLabels: Record<string, string> = {
  submitted: 'Submitted',
  no_submission: 'No saved response',
  not_required: 'No response required',
};

const formatDate = (value?: string | null) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

const extractSignalText = (response: Record<string, any>) => {
  if (!response || typeof response !== 'object') return '';
  if (typeof response.value === 'string' && response.value.trim()) return response.value.trim();
  if (typeof response.final === 'string' && response.final.trim()) return response.final.trim();
  const textParts: string[] = [];
  Object.entries(response).forEach(([key, value]) => {
    if (key === 'reasoning_text' || key === 'confidence_level') return;
    if (typeof value === 'string' && value.trim()) {
      textParts.push(`${key}: ${value.trim()}`);
    }
  });
  return textParts.join('\n');
};

const stepWorkHeading = (step: ReviewStep) => {
  if (step.stage_tag === 'apply') return 'Your saved artifact';
  if (step.counts_as_evidence && !step.content?.assessment_item_type) return 'Your legacy evidence';
  return 'Your saved step work';
};

const stepWorkEmptyState = (step: ReviewStep) => {
  if (step.stage_tag === 'apply') return 'No saved artifact for this step.';
  if (step.counts_as_evidence && !step.content?.assessment_item_type) return 'No legacy evidence saved for this step.';
  return 'No saved response for this step.';
};

export const V2RunReview: React.FC = () => {
  const { runId } = useParams();
  const navigate = useNavigate();
  const redesignEnabled = isUiRedesignPageEnabled('v2_student_run_review');
  const [payload, setPayload] = useState<ReviewPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let canceled = false;
    const load = async () => {
      if (!runId) return;
      setLoading(true);
      setError('');
      try {
        const data = await lessonRunsApi.review(Number(runId));
        if (!canceled) {
          setPayload(data as ReviewPayload);
        }
      } catch (err: any) {
        if (err?.response?.status === 428) {
          navigate('/v2/student', {
            replace: true,
            state: { gate: err?.response?.data?.gate || null },
          });
          return;
        }
        if (!canceled) {
          setError(err?.response?.data?.error || 'Failed to load lesson review.');
          setPayload(null);
        }
      } finally {
        if (!canceled) setLoading(false);
      }
    };
    load();
    return () => {
      canceled = true;
    };
  }, [runId, navigate]);

  const sortedSteps = useMemo(() => {
    const steps = Array.isArray(payload?.steps) ? payload!.steps : [];
    return [...steps].sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return a.id - b.id;
    });
  }, [payload]);

  const shellActions = (
    <Link
      to="/v2/student"
      className={redesignEnabled
        ? 'rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-muted hover:bg-surface-alt hover:text-text'
        : 'text-sm text-indigo-600 hover:text-indigo-800'}
    >
      Back to dashboard
    </Link>
  );

  if (loading) {
    const loadingState = (
      <div className={redesignEnabled ? 'text-sm text-gray-500' : 'max-w-5xl mx-auto p-6 text-sm text-gray-500'}>
        Loading lesson review...
      </div>
    );
    return (
      <OptionalAppShell
        enabled={redesignEnabled}
        role="student"
        navItems={studentV2NavItems('dashboard')}
        actions={shellActions}
        title="Lesson Review"
      >
        {loadingState}
      </OptionalAppShell>
    );
  }

  return (
    <OptionalAppShell
      enabled={redesignEnabled}
      role="student"
      navItems={studentV2NavItems('dashboard')}
      actions={shellActions}
      title="Lesson Review"
    >
      <div className={redesignEnabled ? 'space-y-4' : 'max-w-5xl mx-auto p-6 space-y-4'}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Lesson Review</p>
            <h1 className="text-2xl font-semibold text-gray-900">
              {payload?.run?.lesson_plan_title || 'Lesson Run Review'}
            </h1>
            {payload?.run && (
              <p className="text-sm text-gray-600 mt-1">
                {payload.run.section_name} | Started {formatDate(payload.run.started_at)}
                {payload.run.ended_at ? ` | Ended ${formatDate(payload.run.ended_at)}` : ''}
              </p>
            )}
            {payload?.studio_completion?.display_label ? (
              <p className="text-sm text-slate-600 mt-1">{payload.studio_completion.display_label}</p>
            ) : null}
          </div>
          {!redesignEnabled && shellActions}
        </div>

        {error && (
          <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        )}

        {payload?.source === 'live_plan_fallback' && (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Historical snapshot was unavailable for this run. Review content may reflect the latest lesson version.
          </div>
        )}

        {payload?.run?.allow_catchup && (
          <div className="rounded border border-indigo-200 bg-indigo-50 px-3 py-3 text-sm text-indigo-900">
            {payload?.catchup_assignment?.is_targeted ? (
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="font-semibold">Catch-up assignment available</p>
                  <p className="text-xs text-indigo-800">
                    Mode: {String(payload.catchup_assignment.delivery_mode || '').replace('_', ' ')}
                    {payload.catchup_assignment.due_at ? ` | Due ${formatDate(payload.catchup_assignment.due_at)}` : ''}
                  </p>
                </div>
                <Link
                  to={`/v2/assignments/${payload.catchup_assignment.assignment_id}`}
                  className="inline-flex items-center justify-center rounded bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white"
                >
                  Open catch-up assignment
                </Link>
              </div>
            ) : (
              <p>
                Catch-up is enabled for this run, but you are not currently targeted in an active make-up assignment.
              </p>
            )}
          </div>
        )}

        {payload?.resources?.length ? (
          <ResourceToolkit
            resources={payload.resources}
            lifecycleKey={`review:${payload.run.id}`}
            sessionKey={`review:${payload.run.id}`}
            preserveVideoPosition
            onResourceOpen={(resource) => {
              void lessonRunsApi.resourceOpen(payload.run.id, { title: resource.title, src: resource.src });
            }}
          />
        ) : null}

        {sortedSteps.map((step) => {
          const blocks = Array.isArray(step.content?.blocks) ? step.content.blocks : [];
          const promptMd = typeof step.content?.prompt_md === 'string' ? step.content.prompt_md : '';
          const signalText = step.signal?.response ? extractSignalText(step.signal.response) : '';
          const evidenceText = typeof step.evidence?.content?.text === 'string' ? step.evidence?.content?.text : '';
          return (
            <article key={step.id} className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">
                    Step {step.order + 1} {step.stage_tag ? `| ${String(step.stage_tag).toUpperCase()}` : ''}
                  </p>
                  <h2 className="text-lg font-semibold text-gray-900">{step.title || `Step ${step.order + 1}`}</h2>
                  <div className="mt-1 flex flex-wrap gap-2">
                    <p className="text-xs text-gray-500 capitalize">
                      {step.step_kind.replace('_', ' ')}
                    </p>
                    <span className={`rounded px-2 py-0.5 text-[11px] font-medium ${step.runner_phase === 'studio' ? 'bg-amber-100 text-amber-800' : 'bg-sky-100 text-sky-800'}`}>
                      {step.runner_phase === 'studio' ? 'Independent work' : 'Live'}
                    </span>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusStyles[step.submission_status] || statusStyles.not_required}`}>
                  {statusLabels[step.submission_status] || step.submission_status}
                </span>
              </div>

              {(blocks.length > 0 || promptMd) && (
                <StepRenderer
                  content={step.content}
                  hideHeader
                  mediaSessionKey={`review:${payload?.run.id ?? 'unknown'}:${step.id}`}
                  preserveVideoPosition
                />
              )}

              {step.requires_submission && (
                <div className="rounded border border-slate-200 bg-slate-50 p-3 space-y-2">
                  {step.signal && (
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-500">Your Live Response</p>
                      <pre className="mt-1 text-xs text-slate-700 whitespace-pre-wrap">
                        {signalText || JSON.stringify(step.signal.response || {}, null, 2)}
                      </pre>
                      {step.signal.submitted_at && (
                        <p className="text-[11px] text-slate-500 mt-1">
                          Submitted {formatDate(step.signal.submitted_at)}
                        </p>
                      )}
                    </div>
                  )}
                  {step.evidence && (
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-500">
                        {stepWorkHeading(step)} ({step.evidence.status})
                      </p>
                      <pre className="mt-1 text-xs text-slate-700 whitespace-pre-wrap">
                        {evidenceText || JSON.stringify(step.evidence.content || {}, null, 2)}
                      </pre>
                      {step.evidence.submitted_at && (
                        <p className="text-[11px] text-slate-500 mt-1">
                          Submitted {formatDate(step.evidence.submitted_at)}
                        </p>
                      )}
                    </div>
                  )}
                  {!step.signal && !step.evidence && (
                    <p className="text-sm text-amber-700">{stepWorkEmptyState(step)}</p>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </div>
    </OptionalAppShell>
  );
};
